import { createHash } from "node:crypto";
import { existsSync, readFileSync, writeFileSync } from "node:fs";
import { join } from "node:path";
import { FernignoreMigrator, type MigrationAnalysis } from "../FernignoreMigrator.js";
import { isGenerationCommit, isReplayCommit } from "../git/CommitDetection.js";
import { GitClient } from "../git/GitClient.js";
import { LockfileManager } from "../LockfileManager.js";
import type { CommitInfo, StoredPatch } from "../types.js";

export interface BootstrapOptions {
    /** Log what would happen but don't modify anything */
    dryRun?: boolean;
    /** How to handle .fernignore: "migrate" moves patterns to replay.yml, "delete" removes it, "skip" leaves it */
    fernignoreAction?: "migrate" | "delete" | "skip";
    /** Maximum number of commits to scan for generation history (default: 500) */
    maxCommitsToScan?: number;
    /** Allow overwriting an existing lockfile */
    force?: boolean;
    /** When true, scan git history for existing patches. Default: false (clean start with 0 patches). */
    importHistory?: boolean;
}

export interface BootstrapResult {
    /** The generation commit used as baseline, or null if none found */
    generationCommit: CommitInfo | null;
    /** Number of customization patches detected */
    patchesDetected: number;
    /** Number of patches written to lockfile */
    patchesCreated: number;
    /** The detected patches (for dry-run inspection) */
    patches: StoredPatch[];
    /** .fernignore patterns found, if any */
    fernignorePatterns: string[];
    /** .fernignore migration analysis, if .fernignore exists */
    fernignoreAnalysis?: MigrationAnalysis;
    /** Whether .fernignore was updated to protect replay files */
    fernignoreUpdated: boolean;
    /** Warnings about edge cases */
    warnings: string[];
    /** Number of older generation commits that were skipped (stale commits ignored) */
    staleGenerationsSkipped: number;
    /** The generation commit SHA used as cutoff for patch detection */
    scannedSinceGeneration: string;
}

/**
 * Bootstrap Replay for an existing SDK repository.
 *
 * Scans git history to find all generation commits, identifies
 * user customization patches between them, and creates the initial replay.lock.
 *
 * This is a one-time migration step for SDKs that existed before Replay.
 */
export async function bootstrap(outputDir: string, options?: BootstrapOptions): Promise<BootstrapResult> {
    const git = new GitClient(outputDir);
    const lockManager = new LockfileManager(outputDir);
    const maxCommits = options?.maxCommitsToScan ?? 500;
    const warnings: string[] = [];

    // Check for existing lockfile
    if (lockManager.exists() && !options?.force) {
        return {
            generationCommit: null,
            patchesDetected: 0,
            patchesCreated: 0,
            patches: [],
            fernignorePatterns: [],
            fernignoreUpdated: false,
            warnings: ["Replay lockfile already exists. Use --force to overwrite."],
            staleGenerationsSkipped: 0,
            scannedSinceGeneration: ""
        };
    }

    // 1. Find ALL generation commits (newest first, excluding replay commits)
    const genCommits = await findAllGenerationCommits(git, maxCommits);

    if (genCommits.length === 0) {
        return {
            generationCommit: null,
            patchesDetected: 0,
            patchesCreated: 0,
            patches: [],
            fernignorePatterns: [],
            fernignoreUpdated: false,
            warnings: [
                "No generation commits found in the last " +
                    maxCommits +
                    " commits. " +
                    "Run 'fern generate' first to establish a baseline."
            ],
            staleGenerationsSkipped: 0,
            scannedSinceGeneration: ""
        };
    }

    const latestGen = genCommits[0]!;

    // 2. Set up in-memory lockfile (don't write to disk yet — we may be in dry-run mode)
    // For clean start (no importHistory), use HEAD as current_generation so that
    // the next fern generate detects 0 old commits. When importing history, use
    // the actual generation commit as the base for patch detection.
    const anchorSha = options?.importHistory ? latestGen.sha : (await git.exec(["rev-parse", "HEAD"])).trim();
    const treeHash = await git.getTreeHash(anchorSha);
    const genRecord = {
        commit_sha: anchorSha,
        tree_hash: treeHash,
        timestamp: new Date().toISOString(),
        cli_version: "unknown",
        generator_versions: {} as Record<string, string>
    };

    lockManager.initializeInMemory(genRecord);

    // 3. Scan for user patches (only when importing history)
    const patches: StoredPatch[] = options?.importHistory ? await findAllUserPatches(git, genCommits) : [];

    // 4. Handle .fernignore if present (only when importing history)
    const migrator = new FernignoreMigrator(git, lockManager, outputDir);
    const fernignorePatterns = migrator.readFernignorePatterns();
    let fernignoreAnalysis: MigrationAnalysis | undefined;

    if (options?.importHistory && migrator.fernignoreExists() && fernignorePatterns.length > 0) {
        fernignoreAnalysis = await migrator.analyzeMigration();

        // Add synthetic patches from .fernignore analysis
        if (fernignoreAnalysis.syntheticPatches.length > 0) {
            patches.push(...fernignoreAnalysis.syntheticPatches);
        }

        if (fernignoreAnalysis.fernignoreOnly.length > 0) {
            for (const file of fernignoreAnalysis.fernignoreOnly) {
                warnings.push(
                    `${file}: in .fernignore but no recent commits found since last generation. ` +
                        "File may be stale, matches generated output, or does not exist. No customization to track."
                );
            }
        }
    }

    // In dry-run mode, don't persist anything
    if (options?.dryRun) {
        return {
            generationCommit: latestGen,
            patchesDetected: patches.length,
            patchesCreated: 0,
            patches,
            fernignorePatterns,
            fernignoreAnalysis,
            fernignoreUpdated: false,
            warnings,
            staleGenerationsSkipped: genCommits.length - 1,
            scannedSinceGeneration: latestGen.sha
        };
    }

    // 5. Persist patches to lockfile
    for (const patch of patches) {
        lockManager.addPatch(patch);
    }
    lockManager.save();

    // 6. Ensure .fernignore protects replay files from generation wipe
    const fernignoreUpdated = ensureFernignoreEntries(outputDir);

    // 7. Ensure .gitattributes marks replay.lock as generated (collapses diff in GitHub PRs)
    ensureGitattributesEntries(outputDir);

    // 8. Handle .fernignore migration
    if (migrator.fernignoreExists() && fernignorePatterns.length > 0) {
        const action = options?.fernignoreAction ?? "skip";
        if (action === "migrate") {
            // Move truly untrackable patterns (no diff from generated) to replay.yml exclude list
            const patternsToExclude = fernignoreAnalysis?.fernignoreOnly ?? [];
            if (patternsToExclude.length > 0) {
                migrator.movePatternsToReplayYml(patternsToExclude);
            }
        }
    }

    return {
        generationCommit: latestGen,
        patchesDetected: patches.length,
        patchesCreated: patches.length,
        patches,
        fernignorePatterns,
        fernignoreAnalysis,
        fernignoreUpdated,
        warnings,
        staleGenerationsSkipped: genCommits.length - 1,
        scannedSinceGeneration: latestGen.sha
    };
}

async function findAllGenerationCommits(git: GitClient, maxCommits: number): Promise<CommitInfo[]> {
    const log = await git.exec(["log", "--format=%H%x00%an%x00%ae%x00%s", `-${maxCommits}`]);

    if (!log.trim()) {
        return [];
    }

    const genCommits: CommitInfo[] = [];
    for (const line of log.trim().split("\n")) {
        if (!line) continue;
        const [sha, authorName, authorEmail, message] = line.split("\0");
        const commit: CommitInfo = { sha, authorName, authorEmail, message };
        if (isGenerationCommit(commit) && !isReplayCommit(commit)) {
            genCommits.push(commit);
        }
    }

    return genCommits;
}

async function findAllUserPatches(
    git: GitClient,
    genCommits: CommitInfo[] // newest first
): Promise<StoredPatch[]> {
    const patches: StoredPatch[] = [];
    const seenHashes = new Set<string>();

    // Only scan since the LAST (most recent) generation commit to HEAD
    // This ignores "stale" commits that were regenerated over before the customer activated replay
    const latestGen = genCommits[0]!;
    const recentPatches = await extractUserPatches(git, latestGen.sha, "HEAD", latestGen.sha, seenHashes);
    patches.push(...recentPatches);

    return patches;
}

async function extractUserPatches(
    git: GitClient,
    fromSha: string,
    toRef: string,
    baseGeneration: string,
    seenHashes: Set<string>
): Promise<StoredPatch[]> {
    const log = await git.exec(["log", "--format=%H%x00%an%x00%ae%x00%s", `${fromSha}..${toRef}`]);

    if (!log.trim()) {
        return [];
    }

    const commits = parseGitLog(log);
    const patches: StoredPatch[] = [];

    // Process in reverse (oldest first) for chronological order
    for (const commit of commits.reverse()) {
        if (isGenerationCommit(commit)) continue;

        const parents = await git.getCommitParents(commit.sha);
        if (parents.length > 1) continue;

        const patchContent = await git.formatPatch(commit.sha);
        const contentHash = computeContentHash(patchContent);

        if (seenHashes.has(contentHash)) continue;
        seenHashes.add(contentHash);

        const filesOutput = await git.exec(["diff-tree", "--no-commit-id", "--name-only", "-r", commit.sha]);

        patches.push({
            id: `patch-${commit.sha.slice(0, 8)}`,
            content_hash: contentHash,
            original_commit: commit.sha,
            original_message: commit.message,
            original_author: `${commit.authorName} <${commit.authorEmail}>`,
            base_generation: baseGeneration,
            files: filesOutput.trim().split("\n").filter(Boolean),
            patch_content: patchContent
        });
    }

    return patches;
}

function parseGitLog(log: string): CommitInfo[] {
    return log
        .trim()
        .split("\n")
        .filter(Boolean)
        .map((line) => {
            const [sha, authorName, authorEmail, message] = line.split("\0");
            return { sha, authorName, authorEmail, message };
        });
}

const REPLAY_FERNIGNORE_ENTRIES = [".fern/replay.lock", ".fern/replay.yml", ".gitattributes"];

function ensureFernignoreEntries(outputDir: string): boolean {
    const fernignorePath = join(outputDir, ".fernignore");
    let content = "";

    if (existsSync(fernignorePath)) {
        content = readFileSync(fernignorePath, "utf-8");
    }

    const lines = content.split("\n");
    const toAdd: string[] = [];

    for (const entry of REPLAY_FERNIGNORE_ENTRIES) {
        if (!lines.some((line) => line.trim() === entry)) {
            toAdd.push(entry);
        }
    }

    if (toAdd.length === 0) {
        return false;
    }

    if (content && !content.endsWith("\n")) {
        content += "\n";
    }
    content += toAdd.join("\n") + "\n";
    writeFileSync(fernignorePath, content, "utf-8");
    return true;
}

const GITATTRIBUTES_ENTRIES = [".fern/replay.lock linguist-generated=true"];

function ensureGitattributesEntries(outputDir: string): void {
    const gitattributesPath = join(outputDir, ".gitattributes");
    let content = "";

    if (existsSync(gitattributesPath)) {
        content = readFileSync(gitattributesPath, "utf-8");
    }

    const lines = content.split("\n");
    const toAdd: string[] = [];

    for (const entry of GITATTRIBUTES_ENTRIES) {
        if (!lines.some((line) => line.trim() === entry)) {
            toAdd.push(entry);
        }
    }

    if (toAdd.length === 0) {
        return;
    }

    if (content && !content.endsWith("\n")) {
        content += "\n";
    }
    content += toAdd.join("\n") + "\n";
    writeFileSync(gitattributesPath, content, "utf-8");
}

function computeContentHash(patchContent: string): string {
    const lines = patchContent.split("\n");

    // Strip email wrapper from format-patch output (matches ReplayDetector)
    const diffStart = lines.findIndex((l) => l.startsWith("diff --git "));
    const relevantLines = diffStart > 0 ? lines.slice(diffStart) : lines;

    const normalized = relevantLines
        .filter((line) => !line.startsWith("index "))
        .join("\n")
        .replace(/\n-- \n[\d]+\.[\d]+[^\n]*\s*$/, "");

    return `sha256:${createHash("sha256").update(normalized).digest("hex")}`;
}
